'use client';

import { useState, useEffect, useRef } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { useSearchProducts } from '@/hooks/useProducts';

interface SearchBarProps {
  className?: string;
  defaultExpanded?: boolean;
  enableSuggestions?: boolean;
}

interface SearchSuggestion {
  id: string;
  name: string;
  category: string | string[];
}

const SearchBar = ({
  className = '',
  defaultExpanded = false,
  enableSuggestions = true,
}: SearchBarProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [suggestions, setSuggestions] = useState<SearchSuggestion[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const router = useRouter();
  const { products: searchResults } = useSearchProducts(searchQuery);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsExpanded(false);
        setShowSuggestions(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsExpanded(false);
        setShowSuggestions(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    if (normalizedQuery.length > 0) {
      setSuggestions(
        searchResults
          .filter((item) => {
            const matchesName = item.name.toLowerCase().includes(normalizedQuery);
            const categories = Array.isArray(item.category) ? item.category : [item.category];
            const matchesCategory = categories.some((cat) => cat.toLowerCase().includes(normalizedQuery));
            return matchesName || matchesCategory;
          })
          .slice(0, 8)
      );
      setShowSuggestions(true);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
    setSelectedIndex(-1);
  }, [searchQuery, searchResults]);

  const handleSearch = (query: string) => {
    if (query.trim()) {
      router.push(`/products?search=${encodeURIComponent(query.trim())}`);
      setIsExpanded(false);
      setShowSuggestions(false);
      setSearchQuery('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev < suggestions.length - 1 ? prev + 1 : prev));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && suggestions[selectedIndex]) {
        router.push(`/product-details?id=${encodeURIComponent(suggestions[selectedIndex].id)}`);
        setIsExpanded(false);
        setShowSuggestions(false);
        setSearchQuery('');
      } else {
        handleSearch(searchQuery);
      }
    }
  };

  const handleSuggestionClick = (suggestion: SearchSuggestion) => {
    router.push(`/product-details?id=${encodeURIComponent(suggestion.id)}`);
    setIsExpanded(false);
    setShowSuggestions(false);
    setSearchQuery('');
  };

  const shouldShowSuggestions = enableSuggestions && showSuggestions && suggestions.length > 0;

  return (
    <div data-component-id="src/components/common/SearchBar.tsx:120:4" data-component-path="src/components/common/SearchBar.tsx" data-component-line="120" data-component-end-line="187" data-component-file="SearchBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" ref={searchRef} className={`relative ${className}`}>
      <div data-component-id="src/components/common/SearchBar.tsx:121:6" data-component-path="src/components/common/SearchBar.tsx" data-component-line="121" data-component-end-line="160" data-component-file="SearchBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D"
          className={`flex items-center gap-2 bg-input border border-border rounded-md transition-smooth ${
          isExpanded ? 'w-full lg:w-80' : 'w-10 lg:w-64'
        }`}
      >
        <button data-component-id="src/components/common/SearchBar.tsx:126:8" data-component-path="src/components/common/SearchBar.tsx" data-component-line="126" data-component-end-line="135" data-component-file="SearchBar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22p-2%20text-muted-foreground%20hover%3Atext-foreground%20transition-smooth%20focus%3Aoutline-none%22%7D"
          onClick={() => {
            setIsExpanded(true);
            inputRef.current?.focus();
          }}
          className="p-2 text-muted-foreground hover:text-foreground transition-smooth focus:outline-none"
          aria-label="Search"
        >
          <Icon data-component-id="src/components/common/SearchBar.tsx:134:10" data-component-path="src/components/common/SearchBar.tsx" data-component-line="134" data-component-end-line="134" data-component-file="SearchBar.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MagnifyingGlassIcon%22%7D" name="MagnifyingGlassIcon" size={20} />
        </button>
        <input data-component-id="src/components/common/SearchBar.tsx:136:8" data-component-path="src/components/common/SearchBar.tsx" data-component-line="136" data-component-end-line="147" data-component-file="SearchBar.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22text%22%2C%22value%22%3A%22%5Bvar%3AsearchQuery%5D%22%7D"
          ref={inputRef}
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setIsExpanded(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search products..."
          className={`flex-1 bg-transparent text-foreground placeholder:text-muted-foreground focus:outline-none transition-smooth ${
            isExpanded ? 'w-full opacity-100' : 'w-0 opacity-0 lg:w-full lg:opacity-100'
          }`}
        />
        {searchQuery && (
          <button data-component-id="src/components/common/SearchBar.tsx:149:10" data-component-path="src/components/common/SearchBar.tsx" data-component-line="149" data-component-end-line="158" data-component-file="SearchBar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22p-2%20text-muted-foreground%20hover%3Atext-foreground%20transition-smooth%20focus%3Aoutline-none%22%7D"
            onClick={() => {
              setSearchQuery('');
              inputRef.current?.focus();
            }}
            className="p-2 text-muted-foreground hover:text-foreground transition-smooth focus:outline-none"
            aria-label="Clear search"
          >
            <Icon data-component-id="src/components/common/SearchBar.tsx:157:12" data-component-path="src/components/common/SearchBar.tsx" data-component-line="157" data-component-end-line="157" data-component-file="SearchBar.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22XMarkIcon%22%7D" name="XMarkIcon" size={16} />
          </button>
        )}
      </div>

      {shouldShowSuggestions && (
        <div data-component-id="src/components/common/SearchBar.tsx:163:8" data-component-path="src/components/common/SearchBar.tsx" data-component-line="163" data-component-end-line="185" data-component-file="SearchBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22absolute%20top-full%20left-0%20right-0%20mt-2%20bg-popover%20rounded-md%20shadow-warm-lg%20border%20border-border%20overflow-hidden%20z-dropdown%20animate-slide-in-from-top%20max-h-80%20overflow-y-auto%20scrollbar-warm%22%7D" className="absolute top-full left-0 right-0 mt-2 bg-popover rounded-md shadow-warm-lg border border-border overflow-hidden z-dropdown animate-slide-in-from-top max-h-80 overflow-y-auto scrollbar-warm">
          <div data-component-id="src/components/common/SearchBar.tsx:164:10" data-component-path="src/components/common/SearchBar.tsx" data-component-line="164" data-component-end-line="184" data-component-file="SearchBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22py-2%22%7D" className="py-2">
            {suggestions.map((suggestion, index) => (
              <button data-component-id="src/components/common/SearchBar.tsx:166:14" data-component-path="src/components/common/SearchBar.tsx" data-component-line="166" data-component-end-line="182" data-component-file="SearchBar.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
                key={suggestion.id}
                onClick={() => handleSuggestionClick(suggestion)}
                className={`w-full flex items-center justify-between px-4 py-3 text-left transition-smooth ${
                  index === selectedIndex
                    ? 'bg-primary/10 text-primary' :'text-popover-foreground hover:bg-primary/5'
                } focus:outline-none focus:bg-primary/10`}
              >
                <div data-component-id="src/components/common/SearchBar.tsx:174:16" data-component-path="src/components/common/SearchBar.tsx" data-component-line="174" data-component-end-line="180" data-component-file="SearchBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20gap-3%22%7D" className="flex items-center gap-3">
                  <Icon data-component-id="src/components/common/SearchBar.tsx:175:18" data-component-path="src/components/common/SearchBar.tsx" data-component-line="175" data-component-end-line="175" data-component-file="SearchBar.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22MagnifyingGlassIcon%22%2C%22className%22%3A%22text-muted-foreground%22%7D" name="MagnifyingGlassIcon" size={16} className="text-muted-foreground" />
                  <div data-component-id="src/components/common/SearchBar.tsx:176:18" data-component-path="src/components/common/SearchBar.tsx" data-component-line="176" data-component-end-line="179" data-component-file="SearchBar.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
                    <p data-component-id="src/components/common/SearchBar.tsx:177:20" data-component-path="src/components/common/SearchBar.tsx" data-component-line="177" data-component-end-line="177" data-component-file="SearchBar.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22font-medium%22%7D" className="font-medium">{suggestion.name}</p>
                    <p data-component-id="src/components/common/SearchBar.tsx:178:20" data-component-path="src/components/common/SearchBar.tsx" data-component-line="178" data-component-end-line="178" data-component-file="SearchBar.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22caption%20text-muted-foreground%22%7D" className="caption text-muted-foreground">{suggestion.category}</p>
                  </div>
                </div>
                <Icon data-component-id="src/components/common/SearchBar.tsx:181:16" data-component-path="src/components/common/SearchBar.tsx" data-component-line="181" data-component-end-line="181" data-component-file="SearchBar.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowRightIcon%22%2C%22className%22%3A%22text-muted-foreground%22%7D" name="ArrowRightIcon" size={16} className="text-muted-foreground" />
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchBar;